import { Container, Section } from '@repo/ui'
import { WebLayout } from '@/components/layout'

export function PrivacyPage() {
  return (
    <WebLayout>
      <Section variant="default" className="md:pt-24">
        <Container size="prose">
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            <div className="mb-12">
              <h1 className="text-4xl font-bold tracking-tight mb-2">Privacy Policy</h1>
              <p className="text-muted-foreground text-base">Last updated: February 10, 2026</p>
            </div>

            <div className="bg-muted/30 border border-border rounded-lg p-6 mb-12">
              <p className="text-muted-foreground leading-relaxed text-sm m-0">
                We take your privacy seriously. This policy explains what data we collect, why we
                collect it, and how we protect it. We'll never sell your personal information.
              </p>
            </div>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                <strong className="text-foreground">Account Information:</strong> When you create an
                account, we collect:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
                <li>Email address</li>
                <li>Name (if provided)</li>
                <li>Password (encrypted)</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                <strong className="text-foreground">Usage Data:</strong> We automatically collect:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>IP address and approximate location (country, city)</li>
                <li>Browser type and device information</li>
                <li>Pages visited and features used</li>
                <li>Session duration and frequency</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Data</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use your information to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Provide and maintain the service</li>
                <li>Process payments and send receipts</li>
                <li>Send important updates about the service or your account</li>
                <li>Respond to your support requests</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Improve our service based on usage patterns</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We'll only send marketing emails if you've opted in, and you can unsubscribe
                anytime.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">3. Data Sharing</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We don't sell your data. We only share it when necessary:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>
                  <strong className="text-foreground">Service Providers:</strong> We use trusted
                  third parties for hosting, analytics, and payment processing. They only access
                  data needed for their specific functions.
                </li>
                <li>
                  <strong className="text-foreground">Legal Requirements:</strong> We may disclose
                  data if required by law or to protect our rights and users.
                </li>
                <li>
                  <strong className="text-foreground">Business Transfers:</strong> If we're acquired
                  or merged, your data may transfer to the new entity.
                </li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">4. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We implement industry-standard security measures:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Encryption in transit (HTTPS/TLS)</li>
                <li>Encrypted password storage</li>
                <li>Regular security audits and updates</li>
                <li>Restricted employee access to user data</li>
                <li>Automated backups with encryption</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                While we take security seriously, no system is perfect. If you discover a
                vulnerability, please report it to us at{' '}
                <a
                  href="mailto:security@zippity.app"
                  className="text-primary hover:underline font-medium"
                >
                  security@zippity.app
                </a>
                .
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">5. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                We keep your data while your account is active. After you delete your account, we
                retain data for 30 days to allow recovery, then permanently delete it. We may retain
                some data longer if required by law or for legitimate business purposes (like
                preventing abuse).
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">6. Your Privacy Rights</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You have control over your data:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>
                  <strong className="text-foreground">Access:</strong> View what data we have about
                  you
                </li>
                <li>
                  <strong className="text-foreground">Correction:</strong> Update inaccurate
                  information
                </li>
                <li>
                  <strong className="text-foreground">Deletion:</strong> Request account and data
                  deletion
                </li>
                <li>
                  <strong className="text-foreground">Export:</strong> Download your data in a
                  portable format
                </li>
                <li>
                  <strong className="text-foreground">Opt-out:</strong> Unsubscribe from marketing
                  emails
                </li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                To exercise these rights, contact us at{' '}
                <a
                  href="mailto:privacy@zippity.app"
                  className="text-primary hover:underline font-medium"
                >
                  privacy@zippity.app
                </a>{' '}
                or manage settings in your account dashboard.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">7. Cookies and Analytics</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">We use cookies for:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>
                  <strong className="text-foreground">Essential:</strong> Authentication and
                  security (required)
                </li>
                <li>
                  <strong className="text-foreground">Analytics:</strong> Understanding how you use
                  the service (optional)
                </li>
                <li>
                  <strong className="text-foreground">Preferences:</strong> Remembering your
                  settings (optional)
                </li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                You can control cookie preferences in your browser settings.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">8. International Users</h2>
              <p className="text-muted-foreground leading-relaxed">
                We use edge infrastructure to process data close to you. Your data may be stored and
                processed in multiple locations to provide fast, reliable service. We ensure all
                locations maintain equivalent security standards.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">9. Children's Privacy</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our service is not intended for users under 18. We don't knowingly collect data from
                children. If you believe a child has provided us with information, please contact us
                immediately.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold mb-4">10. Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We'll notify you of material changes via email or in-app notification. You can
                review the latest version of this policy at any time on this page.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                Have questions? Contact our privacy team at{' '}
                <a
                  href="mailto:privacy@zippity.app"
                  className="text-primary hover:underline font-medium"
                >
                  privacy@zippity.app
                </a>
                .
              </p>
            </section>
          </div>
        </Container>
      </Section>
    </WebLayout>
  )
}
